import { useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import cn from 'classnames'
import { Button, ButtonStyles, Typography } from '@grammarly/badger-ui'
import { FormattedMessage } from 'react-intl'

import { TextInput } from '../../../inputs/text-input'
import { FormProvider as UILibFormProvider, RequiredOrOptional } from '../../../ui-lib/form/form-context'
import { useAIDetector } from '../ai-detector-context'
import { AnnounceLoading } from '../../../section/go-templates-tool/form'
import { errorStyle } from '../../../ui-lib/form/error-text'

import { AIDetectorLoading } from '../loading'
import { AIDetectorErrorOrCounter } from '../error-or-counter'
import { AIDetectorDesktopOutput } from './output'
import { AIDetectorDesktopFooter } from './footer'
import { aiDetectorQuestionStructure } from '../utils'
import { AIDetectorTextLimitModal } from '../modals'
import { useExperimentService } from '../../../../lib/hooks'

import styles from './ai-detector-desktop.module.scss'

const numCharsPerLoadingLine = 65
const formId = `form-ai-detector-desktop`
const { C4 } = Typography

export const AIDetectorDesktop = () => {
  const {
    slug,
    methods,
    aiDetectorState,
    aiPercent,
    onSubmit,
    onClear,
    onFormChangeCapture,
    onFormClickCapture,
    isAnonymous,
    isMobileOrTablet,
    gnarTrackFn,
    onChange,
  } = useAIDetector()
  const { isDynamicBannerV2 } = useExperimentService()

  const isDynamicBannerTreatment = useMemo(
    () => isDynamicBannerV2(slug, isMobileOrTablet, isAnonymous),
    [isDynamicBannerV2, slug, isMobileOrTablet, isAnonymous]
  )

  const { handleSubmit, watch } = methods

  const watchText = watch(aiDetectorQuestionStructure.fields.name, '')
  const hasText = (watchText?.length || 0) > 0
  const numLoadingLines = Math.ceil(watchText.length / numCharsPerLoadingLine)

  const isLoading = aiDetectorState === 'loading'

  return (
    <div className={styles.toolContainer}>
      <div aria-live="polite" className={styles.formContainer}>
        <FormProvider {...methods}>
          <UILibFormProvider value={{ formId, requiredOrOptionalIndicator: RequiredOrOptional.Optional }}>
            <form
              id={formId}
              className={cn('font-family-inter', styles.form)}
              onSubmit={handleSubmit(onSubmit)}
              aria-live="polite"
              aria-relevant="additions"
              onChangeCapture={onFormChangeCapture}
              onClickCapture={onFormClickCapture}
              method="post"
            >
              <TextInput
                structure={aiDetectorQuestionStructure}
                className={cn(styles.textarea, isLoading && styles.textareaHidden)}
                labelClassName={styles.labelHidden}
                hideErrorText
                hasLongformText
                onChange={onChange}
              />
              <div className={cn(styles.loadingContainer, !isLoading && styles.loadingContainerHidden)}>
                <AIDetectorLoading numLoadingLines={numLoadingLines} />
              </div>

              <div className={styles.buttonContainer}>
                <Button
                  padding="compact"
                  className={
                    aiDetectorState === 'done'
                      ? ButtonStyles.WhiteButtonGreenTextWithBorder
                      : ButtonStyles.GreenButtonWhiteText
                  }
                  type="submit"
                  status={aiDetectorState === 'loading' ? 'loading' : 'enabled'}
                >
                  <FormattedMessage
                    id="aiDetector.uiAction.checkText"
                    description="AI Detector - UI Action - Check Text"
                    defaultMessage="Check text"
                  />
                </Button>
                {hasText && (
                  <Button
                    padding="compact"
                    className={ButtonStyles.TransparentButtonGrayText}
                    type="button"
                    status={aiDetectorState === 'loading' ? 'disabled' : 'enabled'}
                    onClick={onClear}
                  >
                    <FormattedMessage
                      id="global.uiAction.clear"
                      description="Text for CTA that clears an input"
                      defaultMessage="Clear"
                    />
                  </Button>
                )}

                <div className={styles.rightContainer}>
                  {aiDetectorState === 'error' && hasText ? (
                    <C4 as="span" className={errorStyle}>
                      <FormattedMessage
                        id="error.generic"
                        description="Generic error message"
                        defaultMessage="Something went wrong. Please try again later."
                      />
                    </C4>
                  ) : (
                    <AIDetectorErrorOrCounter structure={aiDetectorQuestionStructure} />
                  )}
                </div>
              </div>
              <AnnounceLoading isLoading={aiDetectorState === 'loading'} />
              <AIDetectorTextLimitModal structure={aiDetectorQuestionStructure} />
            </form>
          </UILibFormProvider>
        </FormProvider>
        <AIDetectorDesktopOutput aiPercent={aiPercent} />
      </div>
      <AIDetectorDesktopFooter
        aiDetectorState={aiDetectorState}
        isDynamicBannerTreatment={isDynamicBannerTreatment}
        gnarTrackFn={gnarTrackFn}
      />
    </div>
  )
}
